import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useMFA } from "@/contexts/MFAContext";
import { useProvider } from "@/contexts/ProviderContext";
import { useSignupConversion } from "@/hooks/useSignupConversion";
import { useRef } from "react";

interface PrivateRouteProps {
  redirectTo?: string;
}

// Pages that are allowed at AAL1 (before MFA is set up or verified)
const MFA_EXEMPT_PATHS = ["/mfa/setup", "/mfa/verify"];

// Pages that don't require tier selection or Jotform connection
const ONBOARDING_EXEMPT_PATHS = [
  "/mfa/setup",
  "/mfa/verify",
  "/connect-jotform",
  "/select-tier",
  "/welcome",
  "/post-checkout",
  "/trial-started",
  "/purchase-success",
];

export const PrivateRoute = ({ redirectTo = "/auth" }: PrivateRouteProps) => {
  const { user, isLoading, subscription, isSubscriptionLoading } = useAuth();
  const { hasEnrolledTOTP, needsMFAVerification, isLoadingMFAStatus } = useMFA();
  const { connectionStatus, isCheckingConnections } = useProvider();
  const location = useLocation();

  // Track if we've ever successfully rendered the outlet
  // This prevents unmounting children during subsequent loading states
  const hasRenderedOutlet = useRef(false);

  // Fire the sign_up conversion once the account is created + email-confirmed
  // and MFA is satisfied — the same stage the old /welcome page implied, but
  // independent of reaching /welcome or the signup-confirming sessionStorage
  // marker (which is skipped for ~70% of confirmed users). Hook is called
  // unconditionally (Rules of Hooks); it no-ops until the user is non-null.
  const signupConversionUser =
    user?.email_confirmed_at && hasEnrolledTOTP && !needsMFAVerification && !isLoadingMFAStatus
      ? user
      : null;
  useSignupConversion(signupConversionUser);

  // Check if current path is MFA-exempt
  const isOnMFAPage = MFA_EXEMPT_PATHS.includes(location.pathname);

  // Check if current path is onboarding-exempt (MFA, connect, tier pages)
  const isOnOnboardingExemptPage = ONBOARDING_EXEMPT_PATHS.includes(location.pathname);

  // Show loading while auth is being determined
  // Only wait for MFA status if we're not on an MFA-exempt page
  const shouldWaitForMFA = !isOnMFAPage && isLoadingMFAStatus;

  // Only show loading spinner on INITIAL load, not on subsequent refreshes
  // This prevents unmounting the MFA enrollment page when auth state refreshes
  const isInitialLoading = (isLoading || isSubscriptionLoading || shouldWaitForMFA) && !hasRenderedOutlet.current;

  if (isInitialLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  // If no user, redirect to auth
  if (!user) {
    return <Navigate to={redirectTo} replace />;
  }

  // MFA enforcement (before anything else)
  // Skip if we're already on an MFA page
  if (!isOnMFAPage) {
    // If user hasn't enrolled MFA, force enrollment
    if (!hasEnrolledTOTP) {
      return <Navigate to="/mfa/setup" replace />;
    }

    // If user has MFA but session needs verification (aal1 -> aal2)
    if (needsMFAVerification) {
      return <Navigate to="/mfa/verify" state={{ from: location.pathname }} replace />;
    }
  }

  // Jotform connection check - after MFA, before tier selection
  // Skip if on onboarding-exempt pages, still checking, or not yet checked (null)
  if (!isOnOnboardingExemptPage && !isCheckingConnections && connectionStatus.jotform === false) {
    return <Navigate to="/connect-jotform" replace />;
  }

  // Tier selection check - skip if on onboarding-exempt pages
  // This must come AFTER Jotform connection check
  if (!isOnOnboardingExemptPage && !subscription?.subscription_tier) {
    return <Navigate to="/select-tier" replace />;
  }

  // Mark that we've rendered the outlet - subsequent loading states won't unmount children
  hasRenderedOutlet.current = true;

  return <Outlet />;
};
